import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import { Node, Path, Flow, Field, Spark } from "@/components/primitives/Primitives";

const principles = [
    { t: "Calm before capability", d: "We choose composure over demonstration. Systems that reveal themselves only when needed." },
    { t: "Judgment as a feature", d: "The frontier of AI is knowing when not to act. We design for restraint." },
    { t: "Legibility", d: "Every model, every agent, every automation should be readable by the people who depend on it." },
    { t: "The local before the global", d: "A model per place. Languages, dialects, and rhythms of everyday life." },
    { t: "Whitespace as design", d: "Interfaces that make room for the human. Silence is a feature." },
    { t: "Human-first outcomes", d: "We measure success by the quality of a working day, not the volume of automation." },
];

export default function About() {
    return (
        <Layout title="About — Ventara" description="A studio of engineers, designers, and researchers building the calm infrastructure of the next decade.">
            <PageHero i18nKey="about"
                eyebrow="About Ventara"
                title={<>A quiet company<br />in a loud field.</>}
                lead="Ventara is a studio of engineers, designers, and researchers building the calm infrastructure of the next decade — the layer where intelligence recedes into the environment and the human moves to the foreground."
            />

            <Section className="pb-32 grid lg:grid-cols-12 gap-16 items-center">
                <div className="lg:col-span-6 text-foreground">
                    <img
                        src="/about-office.webp"
                        alt="Ventara's studio — a glass-walled conference room with the city skyline beyond"
                        className="w-full rounded-3xl aspect-[4/5] object-cover"
                        loading="lazy"
                    />
                </div>
                <div className="lg:col-span-6">
                    <p className="label">Origin</p>
                    <Reveal>
                        <p className="font-serif font-light text-3xl sm:text-4xl leading-[1.15] tracking-tight mt-6">
                            We started Ventara because the software of the last decade shouted. We wanted to build something that could listen.
                        </p>
                    </Reveal>
                    <p className="prose-body mt-8">
                        Our teams are distributed across four continents. Our research is patient. Our products are held to a single standard: they should feel less like tools and more like collaborators. When the work is right, you forget the machine is there at all.
                    </p>
                </div>
            </Section>

            <Section className="py-24 border-t border-border">
                <p className="label">Principles</p>
                <h2 className="font-serif font-light text-4xl sm:text-6xl mt-6 max-w-3xl tracking-tight leading-[1.05]">Six ideas that hold everything else in place.</h2>
                <div className="mt-16 grid md:grid-cols-2 gap-px bg-border">
                    {principles.map((p, i) => (
                        <div key={p.t} className="bg-background p-10" data-testid={`principle-${i}`}>
                            <div className="label">0{i + 1}</div>
                            <h3 className="font-serif text-2xl mt-6 tracking-tight">{p.t}</h3>
                            <p className="prose-body text-sm mt-4">{p.d}</p>
                        </div>
                    ))}
                </div>
            </Section>

            <Section className="py-32 border-t border-border grid lg:grid-cols-12 gap-16 items-center">
                <div className="lg:col-span-7">
                    <p className="label">A studio, not a factory</p>
                    <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 tracking-tight">
                        We stay small on purpose. We publish what we learn. We build what we can defend.
                    </h2>
                </div>
                <div className="lg:col-span-5 grid grid-cols-2 gap-8 text-foreground/70">
                    <Node size={100} />
                    <Path className="w-full h-24" />
                    <Field className="w-32 h-32" />
                    <Spark className="w-28 h-28" />
                </div>
            </Section>
        </Layout>
    );
}
